import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface RetrieverUserField {
  id: string;
  description: string;
  format: string;
}

export interface RetrieverUserGroup {
  id: string;
  description: string;
  children: RetrieverUserField[];
}

export type UserListItem = RetrieverUserField | RetrieverUserGroup;

export function isGroup(item: UserListItem): item is RetrieverUserGroup {
  return 'children' in item && Array.isArray((item as RetrieverUserGroup).children);
}

export function userListToDescriptions(userList: UserListItem[]): Record<string, any> {
  const result: Record<string, any> = {};
  (userList || []).forEach((item, i) => {
    const key = `d_${i}`;
    if (isGroup(item)) {
      const children: Record<string, any> = {};
      (item.children || []).forEach((child, j) => {
        children[`c_${j}`] = {
          id: child.id,
          description: child.description,
          format: child.format ?? '',
          showExtra: false,
        };
      });
      result[key] = { id: item.id, description: item.description, format: '', showExtra: false, children };
    } else {
      result[key] = { id: item.id, description: item.description, format: item.format ?? '', showExtra: false };
    }
  });
  return result;
}

export function descriptionsToUserList(value: Record<string, any> | null | undefined): UserListItem[] {
  if (!value || typeof value !== 'object') return [];
  return Object.values(value).map((item: any) => {
    if (item && item.children && typeof item.children === 'object') {
      const children: RetrieverUserField[] = Object.values(item.children).map((child: any) => ({
        id: child?.id ?? '',
        description: child?.description ?? '',
        format: child?.format ?? '',
      }));
      return { id: item.id ?? '', description: item.description ?? '', children } as RetrieverUserGroup;
    }
    return { id: item?.id ?? '', description: item?.description ?? '', format: item?.format ?? '' } as RetrieverUserField;
  });
}

export interface Retriever {
  id: string;
  name: string;
  content: string;
  instructions: string;
  extraction_default_value: string;
  overwrite_cache: boolean;
  user_list: UserListItem[];
  llm_model: string;
  knowledge: string;
  retriever: string;
  top_k: number | null;
  search_columns?: string[];
  delfos_server_config_id?: string | null;
  created_at?: string;
  updated_at?: string;
  tag?: {
    id: string;
    name: string;
    text_color: string;
    bg_color: string;
  } | null;
}

export interface RetrieverSelectOptions {
  formats: string[];
}

export interface PaginatedRetrieversResponse {
  retrievers: Retriever[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  message?: string;
}

export interface RetrieverExecutionTokens {
  embedding_tokens?: number;
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
}

export interface RetrieverExecutionConfig {
  llm_model?: string | null;
  retriever?: string | null;
  knowledge?: string | null;
  top_k?: number | null;
  delfos_server?: string | null;
  overwrite_cache?: boolean | null;
}

export interface RetrieverExecution {
  id: string;
  retriever_id?: string;
  result?: any;
  tokens?: RetrieverExecutionTokens | null;
  config?: RetrieverExecutionConfig | null;
  user?: string;
  created_at: string;
}

const SELECT_OPTIONS: RetrieverSelectOptions = {
  formats: ['Texto', 'Número', 'Data', 'Booleano', ''],
};

@Injectable({ providedIn: 'root' })
export class RetrieversService {
  private baseUrl = '/api/retrievers/';

  constructor(private http: HttpClient) {}

  getSelectOptions(): RetrieverSelectOptions {
    return SELECT_OPTIONS;
  }

  list(tag?: string, search?: string, page: number = 1, pageSize: number = 10): Observable<PaginatedRetrieversResponse> {
    let url = `${this.baseUrl}?page=${page}&page_size=${pageSize}`;
    if (tag) {
      url += `&tag=${encodeURIComponent(tag)}`;
    }
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    return this.http.get<PaginatedRetrieversResponse>(url);
  }

  getById(retrieverId: string): Observable<Retriever> {
    return this.http
      .get<{ message: string; retriever: Retriever }>(`${this.baseUrl}${retrieverId}`)
      .pipe(map(res => res.retriever));
  }

  create(retriever: Omit<Retriever, 'id' | 'created_at'>): Observable<string> {
    return this.http
      .post<{ message: string; retriever_id: string }>(this.baseUrl, retriever)
      .pipe(map(res => res.retriever_id));
  }

  update(retrieverId: string, retriever: Omit<Retriever, 'id' | 'created_at'>): Observable<string> {
    return this.http.put(`${this.baseUrl}${retrieverId}`, retriever, {
      responseType: 'text',
    });
  }

  rename(retrieverId: string, name: string): Observable<string> {
    return this.http.put(`${this.baseUrl}${retrieverId}`, { name }, {
      responseType: 'text',
    }) as Observable<string>;
  }

  delete(retrieverId: string): Observable<string> {
    return this.http.delete(`${this.baseUrl}${retrieverId}`, {
      responseType: 'text',
    });
  }

  execute(retrieverId: string): Observable<RetrieverExecution> {
    return this.http
      .post<{ message: string; execution: RetrieverExecution }>(`${this.baseUrl}${retrieverId}/execute`, {})
      .pipe(map(res => res.execution));
  }

  getExecutions(retrieverId: string): Observable<RetrieverExecution[]> {
    return this.http
      .get<{ message: string; executions: RetrieverExecution[] }>(`${this.baseUrl}${retrieverId}/executions`)
      .pipe(map(res => res.executions));
  }

  getExecution(retrieverId: string, executionId: string): Observable<RetrieverExecution> {
    return this.http
      .get<{ message: string; execution: RetrieverExecution }>(`${this.baseUrl}${retrieverId}/executions/${executionId}`)
      .pipe(map(res => res.execution));
  }
}
